import { useEffect, useMemo, useRef } from 'react';
import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSession,
} from '../types';
import {
  buildSpectraWarmKey,
  isSpectraPsdCachedForSession,
  resolveSpectraWarmTargets,
  warmSpectraPsdForSession,
} from '../utils/warmSpectraPsd';

type UseWarmSpectraPsdParams = {
  readySessions: SpectralWorkspaceSession[];
  activeViewId: ChartViewId;
  customSegments: CustomTimeSegment[];
  trendStart: string | null;
  trendEnd: string | null;
  selectedSessionId: string | null;
};

export const useWarmSpectraPsd = ({
  readySessions,
  activeViewId,
  customSegments,
  trendStart,
  trendEnd,
  selectedSessionId,
}: UseWarmSpectraPsdParams) => {
  const warmKey = useMemo(
    () =>
      buildSpectraWarmKey({
        readySessions,
        activeViewId,
        customSegments,
        trendStart,
        trendEnd,
        selectedSessionId,
      }),
    [
      readySessions,
      activeViewId,
      customSegments,
      trendStart,
      trendEnd,
      selectedSessionId,
    ]
  );

  const targetsRef = useRef(
    resolveSpectraWarmTargets({
      readySessions,
      activeViewId,
      customSegments,
      trendStart,
      trendEnd,
      selectedSessionId,
    })
  );
  targetsRef.current = resolveSpectraWarmTargets({
    readySessions,
    activeViewId,
    customSegments,
    trendStart,
    trendEnd,
    selectedSessionId,
  });

  useEffect(() => {
    const { segmentBounds, sessions } = targetsRef.current;
    if (!segmentBounds || sessions.length === 0) return;

    let cancelled = false;
    let index = 0;
    const idleIds: number[] = [];
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];

    const warmNext = () => {
      if (cancelled) return;

      while (index < sessions.length) {
        const session = sessions[index];
        index += 1;

        if (!isSpectraPsdCachedForSession(session, segmentBounds)) {
          warmSpectraPsdForSession(session, segmentBounds);
          scheduleNext();
          return;
        }
      }
    };

    const scheduleNext = () => {
      if (cancelled || index >= sessions.length) return;

      if ('requestIdleCallback' in window) {
        idleIds.push(
          window.requestIdleCallback(() => warmNext(), { timeout: 300 })
        );
        return;
      }

      timeoutIds.push(globalThis.setTimeout(warmNext, 0));
    };

    const hasUncached = sessions.some(
      session => !isSpectraPsdCachedForSession(session, segmentBounds)
    );
    if (hasUncached) {
      scheduleNext();
    }

    return () => {
      cancelled = true;
      idleIds.forEach(id => window.cancelIdleCallback(id));
      timeoutIds.forEach(id => window.clearTimeout(id));
    };
  }, [warmKey]);
};
